import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { grey, red, yellow } from "@mui/material/colors";
import { formatNumberByThousands } from "helpers/numbers";
import LightModeIcon from "@mui/icons-material/LightMode";
import WindPowerIcon from "@mui/icons-material/WindPower";
import ForestIcon from "@mui/icons-material/Forest";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchReportsQueryOptions,
  fetchGOYearlySummary,
  fetchGOSummary,
  fetchLastGoSyncTime,
} from "store/appSlice";
import {
  selectGOYearlySummary,
  selectGOSummary,
  selectLastGoSyncTime,
} from "store/selectors";
import Filters from "./Filters";
import { upperCase } from "lodash";
import { AttachFile } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { exportGOSummary, syncGOs } from "../../../api/resources/go";
import { downloadXLSX } from "../../../helpers/excelExport";

const SUMMARY_MONTH_COUNT = 19;
const BLOCK_NEWEST_MONTHS_AGO = 13;

const getSummaryMonths = () => {
  const months = [];
  for (let i = 0; i < SUMMARY_MONTH_COUNT; i++) {
    months.push(dayjs().subtract(i, "month"));
  }
  return months;
};

const GuaranteesOfOriginSummary = () => {
  const yearlySummary = useAppSelector(selectGOYearlySummary);
  const summary = useAppSelector(selectGOSummary);
  const lastGoSyncTime = useAppSelector(selectLastGoSyncTime);
  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    country: "EE",
    fuelType: "all",
    productionSupport: "all" as "all" | "yes" | "no",
    investmentSupport: "all" as "all" | "yes" | "no",
    filterByGroups: false,
  });
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchReportsQueryOptions());
    dispatch(fetchGOYearlySummary());
    dispatch(fetchLastGoSyncTime());
    dispatch(
      fetchGOSummary({
        country: filters.country,
        technology: filters.fuelType,
        productionSupport:
          filters.productionSupport === "all"
            ? undefined
            : filters.productionSupport === "yes",
        investmentSupport:
          filters.investmentSupport === "all"
            ? undefined
            : filters.investmentSupport === "yes",
        filterByGroups: filters.filterByGroups,
      }),
    );
  }, [dispatch, filters]);

  const handleExport = async () => {
    setLoading(true);
    try {
      // Process filters the same way as fetchGOSummary
      const exportFilters = {
        country: filters.country,
        technology: filters.fuelType === "all" ? undefined : filters.fuelType,
        productionSupport:
          filters.productionSupport === "all"
            ? undefined
            : filters.productionSupport === "yes",
        investmentSupport:
          filters.investmentSupport === "all"
            ? undefined
            : filters.investmentSupport === "yes",
        filterByGroups: filters.filterByGroups,
      };

      const data = await exportGOSummary(exportFilters);
      const filename = `GO_Summary_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      await downloadXLSX(data, filename);
    } catch (error) {
      console.error("Error exporting summary:", error);
      setSnackbar({
        type: "error",
        message: "Failed to export summary",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncGOs = () => {
    syncGOs().catch((error) => console.error("GO sync failed:", error));
    setSnackbar({
      type: "info",
      message: "GO sync started. This may take a few minutes.",
    });
  };

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const currentYear = dayjs().year();

  const years = yearlySummary
    ? [
        {
          year: currentYear,
          total: yearlySummary[currentYear].total,
          solar: yearlySummary[currentYear].solar,
          wind: yearlySummary[currentYear].wind,
          biomass: yearlySummary[currentYear].biomass,
          other: yearlySummary[currentYear].other,
        },
        {
          year: currentYear - 1,
          total: yearlySummary[currentYear - 1].total,
          solar: yearlySummary[currentYear - 1].solar,
          wind: yearlySummary[currentYear - 1].wind,
          biomass: yearlySummary[currentYear - 1].biomass,
          other: yearlySummary[currentYear - 1].other,
        },
        {
          year: currentYear - 2,
          total: yearlySummary[currentYear - 2].total,
          solar: yearlySummary[currentYear - 2].solar,
          wind: yearlySummary[currentYear - 2].wind,
          biomass: yearlySummary[currentYear - 2].biomass,
          other: yearlySummary[currentYear - 2].other,
        },
      ]
    : [];

  const months = getSummaryMonths();

  const columns = [
    {
      accessorKey: "name",
      header: filters.fuelType ? upperCase(filters.fuelType) : "ALL FUEL TYPES",
      sticky: true,
    },
    ...(!filters.filterByGroups
      ? [
          {
            accessorKey: "group",
            header: "Group",
            sticky: true,
          },
        ]
      : []),
    {
      accessorKey: `year_${currentYear}`,
      header: String(currentYear),
    },
    {
      accessorKey: `year_${currentYear - 1}`,
      header: String(currentYear - 1),
    },
    {
      accessorKey: "months_12_18",
      header: "12-18 months",
    },
    ...months.map((month) => ({
      accessorKey: `month_${dayjs(month).format("YYYY_MM")}`,
      header: dayjs(month).format("MMM YYYY"),
    })),
  ];

  const hasGroup = (row: { groupName?: string | null }) =>
    !!row.groupName && row.groupName.trim().length > 0;

  const sortedSummaryRows = summary?.rows
    ? [...summary.rows].sort((a, b) => {
        if (filters.filterByGroups) {
          const aGrouped = hasGroup(a);
          const bGrouped = hasGroup(b);
          if (aGrouped !== bGrouped) return aGrouped ? -1 : 1;
          const aName = aGrouped ? a.groupName : a.clientName;
          const bName = bGrouped ? b.groupName : b.clientName;
          return (aName || "").localeCompare(bName || "");
        }
        return (a.clientName || "").localeCompare(b.clientName || "");
      })
    : [];

  const rows = summary?.rows
    ? [
        // Add total row first
        {
          name: "Total",
          ...(!filters.filterByGroups && { group: "TOTAL" }),
          [`year_${currentYear}`]: summary.total.producedThisYear || 0,
          [`year_${currentYear - 1}`]: summary.total.producedLastYear || 0,
          months_12_18: summary.total.produced12to18MonthsAgo || 0,
          ...months.reduce(
            (acc, month) => {
              const monthKey = dayjs(month).format("YYYY-MM");
              acc[`month_${dayjs(month).format("YYYY_MM")}`] =
                summary.total.monthly?.[monthKey] || 0;
              return acc;
            },
            {} as Record<string, number>,
          ),
        },
        // Add client rows
        ...sortedSummaryRows.map((row) => {
          const data: Record<string, any> = {
            name: filters.filterByGroups
              ? hasGroup(row)
                ? row.groupName
                : row.clientName
              : row.clientName,
            ...(!filters.filterByGroups && { group: row.groupName }),
            [`year_${currentYear}`]: row.producedThisYear || 0,
            [`year_${currentYear - 1}`]: row.producedLastYear || 0,
            months_12_18: row.produced12to18MonthsAgo || 0,
          };
          months.forEach((month) => {
            const monthKey = dayjs(month).format("YYYY-MM");
            data[`month_${dayjs(month).format("YYYY_MM")}`] =
              row.monthly?.[monthKey] || 0;
          });
          return data;
        }),
      ]
    : [];

  // Months are ordered newest → oldest. A border is placed at each calendar
  // quarter boundary so the table is grouped by Q1/Q2/Q3/Q4 instead of every
  // three months from "now".
  const monthsStartIndex = filters.filterByGroups ? 4 : 5;
  const blockStartIndex = monthsStartIndex + BLOCK_NEWEST_MONTHS_AGO;
  const quarterOf = (monthIndex: number) => Math.floor(monthIndex / 3) + 1;
  const yearSeparatorIndexes = filters.filterByGroups ? [2, 3] : [3, 4];
  const monthQuarterBoundaries = months.reduce<number[]>((acc, month, i) => {
    if (i === months.length - 1) return acc;
    if (quarterOf(month.month()) !== quarterOf(months[i + 1].month())) {
      acc.push(monthsStartIndex + i);
    }
    return acc;
  }, []);
  const hasRightBorder = [...yearSeparatorIndexes, ...monthQuarterBoundaries];

  // Alternate grey shading per quarter group so it aligns with the borders.
  const shadedQuarterCols = new Set<number>();
  let quarterGroupIndex = 0;
  months.forEach((month, i) => {
    if (i > 0 && quarterOf(month.month()) !== quarterOf(months[i - 1].month())) {
      quarterGroupIndex++;
    }
    if (quarterGroupIndex % 2 === 0) {
      shadedQuarterCols.add(monthsStartIndex + i);
    }
  });

  return (
    <MainLayout
      tabs={[
        { label: "Summary", path: "GuaranteesOfOriginSummary" },
        { label: "Certificates", path: "GuaranteesOfOriginCertificates" },
      ]}
      snackbar={{
        open: snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="textSecondary">
            Guarantees of Origin
          </Typography>
          <Typography variant="h5">Summary</Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-end"
        >
          <Box display="flex" alignItems="center" sx={{ mb: "0.4rem" }}>
            <Typography variant="body2" color="textSecondary">
              {lastGoSyncTime
                ? `Last updated: ${dayjs(
                    lastGoSyncTime.lastSyncFinishedAt + "Z",
                  ).format("DD.MM.YYYY [at] HH:mm")}`
                : "Last updated: -"}
            </Typography>
            <Tooltip title="Sync GO-s">
              <IconButton size="small" onClick={handleSyncGOs} sx={{ ml: 0.5 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <LoadingButton
            loading={loading}
            variant="contained"
            size="small"
            startIcon={<AttachFile />}
            onClick={handleExport}
          >
            Export
          </LoadingButton>
        </Box>
      </Box>
      <Box sx={{ mt: "2rem" }}>
        <Grid container spacing={2}>
          {years.map((year) => (
            <Grid item sm={4}>
              <Box sx={{ padding: "2rem", background: grey[200] }}>
                <Typography variant="body2" color="textSecondary">
                  {year.year}
                </Typography>
                <Typography component="h4" variant="h4">
                  {formatNumberByThousands(year.total)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: "2rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <LightModeIcon sx={{ mr: 0.5 }} />
                    {formatNumberByThousands(year.solar)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <WindPowerIcon sx={{ mr: 0.5 }} />
                    {formatNumberByThousands(year.wind)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <ForestIcon sx={{ mr: 0.5 }} />
                    {formatNumberByThousands(year.biomass)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <MoreHorizIcon sx={{ mr: 0.5 }} />
                    {formatNumberByThousands(year.other)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box
          mt={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Filters filters={filters} setFilters={setFilters} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <TableContainer
            sx={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
            }}
          >
            <Table stickyHeader dense size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={col.accessorKey}
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "right",
                        backgroundColor: grey[200],
                        ...(colIndex === 0 && {
                          position: "sticky",
                          left: 0,
                          fontWeight: "bold",
                          textAlign: "left",
                          zIndex: 10,
                          boxShadow: "2px 0 5px -2px rgba(0,0,0,0.3)",
                        }),
                        ...(colIndex === 1 && {
                          fontWeight: "bold",
                          textAlign: "left",
                          borderRight: "1px solid" + grey[600],
                        }),
                        ...(hasRightBorder.includes(colIndex) && {
                          borderRight: "1.5px solid " + grey[600],
                        }),
                      }}
                    >
                      {col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <TableCell
                        key={col.accessorKey}
                        sx={{
                          textAlign: "right",
                          ...(colIndex === 0 && {
                            position: "sticky",
                            backgroundColor: grey[50],
                            textAlign: "left",
                            left: 0,
                            minWidth: "220px",
                            fontWeight: "medium",
                            zIndex: 9,
                            boxShadow: "2px 0 5px -2px rgba(0,0,0,0.2)",
                          }),
                          ...(colIndex === 1 && {
                            textAlign: "left",
                            minWidth: "165px",
                            borderRight: "1px solid" + grey[600],
                          }),
                          ...(colIndex === 2 && {
                            fontWeight: "bold",
                          }),
                          ...(colIndex === 3 && {
                            borderRight: "1px solid" + grey[400],
                            fontWeight: "bold",
                          }),
                          ...(colIndex === 4 && {
                            borderRight: "1px solid" + grey[400],
                            fontWeight: "bold",
                          }),
                          ...(shadedQuarterCols.has(colIndex) && {
                            backgroundColor: grey[100],
                          }),
                          ...(colIndex >= blockStartIndex && {
                            backgroundColor: red[50],
                          }),
                          ...(rowIndex === 0 && {
                            backgroundColor: yellow[100],
                            fontWeight: "bold",
                            borderBottom: "2px solid " + grey[600],
                          }),
                          ...(hasRightBorder.includes(colIndex) && {
                            borderRight: "1px solid " + grey[600],
                          }),
                        }}
                      >
                        {row[col.accessorKey]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default GuaranteesOfOriginSummary;
